import { Plus, SearchLg } from '@untitledui/icons';
import type {
  FocusEventHandler,
  KeyboardEvent,
  PointerEventHandler,
  RefAttributes,
  RefObject,
} from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FocusScope, useFilter, useFocusManager } from 'react-aria';
import type {
  ComboBoxProps as AriaComboBoxProps,
  ComboBoxRenderProps,
  GroupProps as AriaGroupProps,
  Key,
  ListBoxProps as AriaListBoxProps,
} from 'react-aria-components';
import {
  ComboBox as AriaComboBox,
  ComboBoxStateContext,
  Group as AriaGroup,
  Input as AriaInput,
  ListBox as AriaListBox,
} from 'react-aria-components';
import type { ListData } from 'react-stately';
import { useListData } from 'react-stately';

import { useResizeObserver } from '@/hooks/useResizeObserver';
import { Avatar } from '@/lib/base/avatar/avatar';
import type { IconComponentType } from '@/lib/base/badges/badge-types';
import { HintText } from '@/lib/base/forms/hint-text';
import { Label } from '@/lib/base/forms/input/label';
import {
  type SelectItemType,
  type SelectSize,
  sizes,
} from '@/lib/base/forms/select/select';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Popover } from '@/lib/base/overlays/popover';
import { TagCloseX } from '@/lib/base/tags/tag-close-x';
import { cx } from '@/utils/cx';

import { SelectItem } from './select-item';

interface ComboBoxValueProps extends AriaGroupProps {
  size: SelectSize;
  shortcut?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  shortcutClassName?: string;
  placeholderIcon?: IconComponentType | null;
  ref?: RefObject<HTMLDivElement | null>;
  onFocus?: FocusEventHandler;
  onPointerEnter?: PointerEventHandler;
}

/** Custom tag renderer function type */
export type RenderTagFn = (
  item: SelectItemType,
  onRemove: (id: Key) => void
) => React.ReactNode;

const ComboboxContext = createContext<{
  size: SelectSize;
  selectedKeys: Key[];
  selectedItems: ListData<SelectItemType>;
  onRemove: (keys: Set<Key>) => void;
  onInputChange: (value: string) => void;
  renderTag?: RenderTagFn;
  inputRef?: RefObject<HTMLInputElement | null>;
}>({
  size: 'sm',
  selectedKeys: [],
  selectedItems: {} as ListData<SelectItemType>,
  onRemove: () => {},
  onInputChange: () => {},
  renderTag: undefined,
  inputRef: undefined,
});

interface MultiSelectProps
  extends
    Omit<AriaComboBoxProps<SelectItemType>, 'children' | 'items'>,
    RefAttributes<HTMLDivElement> {
  hint?: string;
  label?: string;
  tooltip?: string;
  size?: SelectSize;
  placeholder?: string;
  shortcut?: boolean;
  items?: SelectItemType[];
  popoverClassName?: string;
  shortcutClassName?: string;
  selectedItems: ListData<SelectItemType>;
  placeholderIcon?: IconComponentType | null;
  children: AriaListBoxProps<SelectItemType>['children'];
  onItemCleared?: (key: Key) => void;
  onItemInserted?: (key: Key) => void;
  /** Ref to the inner text input, so a host can focus the field it actually renders. */
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Custom renderer for selected tags */
  renderTag?: RenderTagFn;
  /** Maximum number of selections allowed. When reached, new selection replaces oldest. */
  maxSelection?: number;
  /** Callback when "Create new" is clicked. Renders a create option at the bottom of the dropdown. */
  onCreateNew?: () => void;
  /** Label for the create new option. Defaults to "Create new". */
  createNewLabel?: string;
  /** Show a loading indicator in the dropdown when items are being fetched. */
  isLoading?: boolean;
  /** Text to display when there are no items and not loading. */
  emptyText?: string;
  /**
   * Suppress the options list (and its loading/empty states) entirely, leaving
   * only the "Create new" row. For targets where selecting an EXISTING record
   * is never a valid operation, so showing a list can only mislead.
   *
   * Pair with `onCreateNew` — with the list gone, that is the only way to add a
   * value, and without it the dropdown renders nothing at all.
   */
  createOnly?: boolean;
}

export const MultiSelectBase = ({
  items,
  children,
  size = 'sm',
  selectedItems,
  onItemCleared,
  onItemInserted,
  shortcut,
  placeholder = 'Search',
  renderTag,
  maxSelection,
  onCreateNew,
  createNewLabel = 'Create new',
  isLoading = false,
  emptyText,
  createOnly = false,
  inputRef,
  // Omit these props to avoid conflicts with the `Select` component
  name: _name,
  className: _className,
  ...props
}: MultiSelectProps) => {
  const { contains } = useFilter({ sensitivity: 'base' });
  const selectedKeys = useMemo(
    () => selectedItems.items.map(item => item.id),
    [selectedItems.items]
  );

  const filter = useCallback(
    (item: SelectItemType, filterText: string) => {
      return (
        !selectedKeys.includes(item.id) &&
        contains(item.label || item.supportingText || '', filterText)
      );
    },
    [contains, selectedKeys]
  );

  const accessibleList = useListData({
    initialItems: items,
    filter,
  });

  // Sync accessibleList when the items *prop* changes (e.g. new member list).
  // Previous implementation compared accessibleList.items (filtered by selected
  // keys) against the unfiltered items prop — they could never match when items
  // were selected, causing an infinite remove→append→re-render loop.  Fix:
  // track the previous items prop via ref and only sync when it actually changes.
  const prevItemsRef = useRef(items);
  useEffect(() => {
    const prevIds = (prevItemsRef.current || []).map(item => String(item.id));
    const newIds = (items || []).map(item => String(item.id));

    if (JSON.stringify(prevIds) !== JSON.stringify(newIds)) {
      // Remove previous items by their IDs (works on the backing store,
      // including items hidden by the filter function).
      if (prevIds.length > 0) {
        accessibleList.remove(...prevIds);
      }
      if (items && items.length > 0) {
        accessibleList.append(...items);
      }
      prevItemsRef.current = items;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accessibleList is a new ref each render; only re-run when the items prop changes
  }, [items]);

  const onRemove = useCallback(
    (keys: Set<Key>) => {
      const key = keys.values().next().value;

      if (!key) {
        return;
      }

      selectedItems.remove(key);
      onItemCleared?.(key);
    },
    [selectedItems, onItemCleared]
  );

  const onSelectionChange = useCallback(
    (id: Key | null) => {
      if (!id) {
        return;
      }

      const item = accessibleList.getItem(id);

      if (!item) {
        return;
      }

      if (!selectedKeys.includes(id as string)) {
        // Handle maxSelection: if at limit, replace oldest selection
        if (maxSelection && selectedItems.items.length >= maxSelection) {
          const oldestItem = selectedItems.items[0];
          if (oldestItem) {
            selectedItems.remove(oldestItem.id);
            onItemCleared?.(oldestItem.id);
          }
        }
        selectedItems.append(item);
        onItemInserted?.(id);
      }

      accessibleList.setFilterText('');
    },
    [
      accessibleList,
      selectedKeys,
      maxSelection,
      selectedItems,
      onItemCleared,
      onItemInserted,
    ]
  );

  const onInputChange = useCallback(
    (value: string) => {
      accessibleList.setFilterText(value);
    },
    [accessibleList]
  );

  const placeholderRef = useRef<HTMLDivElement>(null);
  const [popoverWidth, setPopoverWidth] = useState('');

  // Resize observer for popover width
  const onResize = useCallback(() => {
    if (!placeholderRef.current) {
      return;
    }
    const divRect = placeholderRef.current?.getBoundingClientRect();
    setPopoverWidth(divRect.width + 'px');
  }, [placeholderRef, setPopoverWidth]);

  const handleCreateNewPointerDown = useCallback((e: React.PointerEvent) => {
    // Prevent focus from leaving the combobox input,
    // which would close the popover before onClick fires.
    e.preventDefault();
  }, []);

  const handleCreateNewClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Close the combobox popover before invoking the callback.
      // The popover is a portal anchored to the combobox trigger.
      // If the callback opens a modal that hides the trigger
      // (e.g. via display:none), the portal repositions to (0,0)
      // causing a visible flash in the top-left corner.
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      onCreateNew?.();
    },
    [onCreateNew]
  );

  useResizeObserver({
    ref: placeholderRef,
    onResize: onResize,
    box: 'border-box',
  });

  let listContent: React.ReactNode;
  if (createOnly) {
    listContent = null;
  } else if (isLoading) {
    listContent = (
      <div className='flex items-center justify-center py-4'>
        <LoadingIndicator size='2xs' />
      </div>
    );
  } else if (accessibleList.items.length === 0 && emptyText) {
    listContent = (
      <div className='px-3 py-4 text-center text-sm text-tertiary'>
        {emptyText}
      </div>
    );
  } else {
    listContent = (
      <AriaListBox
        selectionMode='multiple'
        className='size-full outline-hidden'
      >
        {children}
      </AriaListBox>
    );
  }

  const contextValue = useMemo(
    () => ({
      size,
      selectedKeys,
      selectedItems,
      onInputChange,
      onRemove,
      renderTag,
      inputRef,
    }),
    [
      size,
      selectedKeys,
      selectedItems,
      onInputChange,
      onRemove,
      renderTag,
      inputRef,
    ]
  );

  return (
    <ComboboxContext.Provider value={contextValue}>
      <AriaComboBox
        allowsEmptyCollection
        menuTrigger='focus'
        items={accessibleList.items}
        onInputChange={onInputChange}
        inputValue={accessibleList.filterText}
        // This keeps the combobox popover open and the input value unchanged when an item is selected.
        selectedKey={null}
        onSelectionChange={onSelectionChange}
        aria-label={props.label}
        {...props}
      >
        {(state: ComboBoxRenderProps) => (
          <div className='flex flex-col gap-1.5'>
            {props.label && (
              <Label isRequired={state.isRequired} tooltip={props.tooltip}>
                {props.label}
              </Label>
            )}

            <MultiSelectTagsValue
              size={size}
              shortcut={shortcut}
              ref={placeholderRef}
              placeholder={placeholder}
              placeholderIcon={props.placeholderIcon}
              // This is a workaround to correctly calculating the trigger width
              // while using ResizeObserver wasn't 100% reliable.
              onFocus={onResize}
              onPointerEnter={onResize}
            />

            <Popover
              size='md'
              triggerRef={placeholderRef}
              style={{ width: popoverWidth }}
              className={props?.popoverClassName}
            >
              <div className='flex flex-col h-full'>
                <div className='flex-1 min-h-0 overflow-y-auto'>
                  {listContent}
                </div>
                {onCreateNew && (
                  <div className='sticky bottom-0 w-full px-1.5 py-px border-t border-secondary bg-primary'>
                    <button
                      type='button'
                      className='flex w-full cursor-pointer items-center gap-2 rounded-md p-2 pr-2.5 hover:bg-primary_hover outline-hidden focus:ring-2 focus:ring-focus-ring focus:ring-inset'
                      onPointerDown={handleCreateNewPointerDown}
                      onClick={handleCreateNewClick}
                    >
                      <Plus className='size-5 shrink-0 text-fg-quaternary' />
                      <span className='text-md font-medium text-tertiary'>
                        {createNewLabel}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </Popover>

            {props.hint && (
              <HintText isInvalid={state.isInvalid}>{props.hint}</HintText>
            )}
          </div>
        )}
      </AriaComboBox>
    </ComboboxContext.Provider>
  );
};

/**
 * Keyboard handling for one selected-value tag: Backspace/Enter/Space
 * removes it (moving focus to the adjacent tag), ArrowLeft/ArrowRight
 * navigate between tags, and Escape closes the dropdown.
 *
 * Every rendered tag — including custom `renderTag` implementations, e.g.
 * `MultiSelectInput`'s option chips — must call this so keyboard removal
 * behaves the same everywhere a chip appears. Must be called from its own
 * component instance (never from inside another component's render loop):
 * `InnerMultiSelect` renders N tags via `.map()`, and calling a hook a
 * variable number of times within one component's render violates the Rules
 * of Hooks — see `DefaultTag` below, and `OptionTag` in `MultiSelectInput`,
 * for the pattern of wrapping each tag in its own component so the hook call
 * is one-per-instance rather than one-per-loop-iteration.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook, not a component
export function useTagKeyDown(value: Key) {
  const focusManager = useFocusManager();
  const comboBoxContext = useContext(ComboboxContext);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ComboBoxStateContext internal type not exported by react-aria-components
  const comboBoxStateContext = useContext(ComboBoxStateContext) as any;

  return useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      // Do nothing when tab is clicked to move focus from the tag to the input element.
      if (event.key === 'Tab') {
        return;
      }

      event.preventDefault();

      const isFirstTag =
        comboBoxContext?.selectedItems?.items?.[0]?.id === value;

      switch (event.key) {
        case ' ':
        case 'Enter':
        case 'Backspace':
          if (isFirstTag) {
            focusManager?.focusNext({ wrap: false, tabbable: false });
          } else {
            focusManager?.focusPrevious({ wrap: false, tabbable: false });
          }

          comboBoxContext.onRemove(new Set([value]));
          break;

        case 'ArrowLeft':
          focusManager?.focusPrevious({ wrap: false, tabbable: false });
          break;
        case 'ArrowRight':
          focusManager?.focusNext({ wrap: false, tabbable: false });
          break;
        case 'Escape':
          comboBoxStateContext?.close();
          break;
      }
    },
    [comboBoxContext, focusManager, comboBoxStateContext, value]
  );
}

interface DefaultTagProps {
  value: SelectItemType;
  isDisabled?: boolean;
}

/** Default tag renderer, used when `MultiSelect` is given no custom `renderTag`. */
function DefaultTag({ value, isDisabled }: DefaultTagProps) {
  const comboBoxContext = useContext(ComboboxContext);
  const handleKeyDown = useTagKeyDown(value.id);

  return (
    <span className='flex items-center rounded-md bg-primary py-0.5 pr-1 pl-1.25 ring-1 ring-primary ring-inset'>
      <Avatar size='xxs' alt={value?.label} src={value?.avatarUrl} />

      <p className='ml-1.25 truncate text-sm whitespace-nowrap text-secondary select-none'>
        {value?.label}
      </p>

      <TagCloseX
        size='md'
        isDisabled={isDisabled}
        className='ml-0.75'
        // For workaround, onKeyDown is added to the button
        onKeyDown={handleKeyDown}
        onPress={() => comboBoxContext.onRemove(new Set([value.id]))}
      />
    </span>
  );
}

const InnerMultiSelect = ({
  isDisabled,
  shortcut,
  shortcutClassName,
  placeholder,
}: Omit<MultiSelectProps, 'selectedItems' | 'children'>) => {
  const focusManager = useFocusManager();
  const comboBoxContext = useContext(ComboboxContext);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ComboBoxStateContext internal type not exported by react-aria-components
  const comboBoxStateContext = useContext(ComboBoxStateContext) as any;

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const isCaretAtStart =
        event.currentTarget.selectionStart === 0 &&
        event.currentTarget.selectionEnd === 0;

      if (!isCaretAtStart && event.currentTarget.value !== '') {
        return;
      }

      switch (event.key) {
        case 'Backspace':
        case 'ArrowLeft':
          focusManager?.focusPrevious({ wrap: false, tabbable: false });
          break;
        case 'ArrowRight':
          focusManager?.focusNext({ wrap: false, tabbable: false });
          break;
      }
    },
    [focusManager]
  );

  // Ensure dropdown opens on click even if input is already focused
  const handleInputMouseDown = useCallback(
    (_event: React.MouseEvent<HTMLInputElement>) => {
      if (comboBoxStateContext && !comboBoxStateContext.isOpen) {
        comboBoxStateContext.open();
      }
    },
    [comboBoxStateContext]
  );

  const isSelectionEmpty = comboBoxContext?.selectedItems?.items?.length === 0;

  const handleRemove = useCallback(
    (id: Key) => {
      comboBoxContext.onRemove(new Set([id]));
    },
    [comboBoxContext]
  );

  return (
    <div className='relative flex w-full flex-1 flex-row flex-wrap items-center justify-start gap-1.5'>
      {!isSelectionEmpty &&
        comboBoxContext?.selectedItems?.items?.map(value =>
          comboBoxContext.renderTag ? (
            comboBoxContext.renderTag(value, handleRemove)
          ) : (
            <DefaultTag key={value.id} value={value} isDisabled={isDisabled} />
          )
        )}

      <div
        className={cx(
          'relative flex min-w-16 flex-1 flex-row items-center',
          !isSelectionEmpty && 'ml-0.5',
          shortcut && 'min-w-[30%]'
        )}
      >
        <AriaInput
          ref={comboBoxContext.inputRef}
          placeholder={placeholder}
          onKeyDown={handleInputKeyDown}
          onMouseDown={handleInputMouseDown}
          className={cx(
            'w-full flex-[1_0_0] appearance-none bg-transparent text-ellipsis text-primary caret-alpha-black/90 outline-none placeholder:text-placeholder focus:outline-hidden disabled:cursor-not-allowed disabled:text-disabled disabled:placeholder:text-disabled',
            // Sized from context: this input is a grandchild of the box, so it
            // does not inherit the `root` font size.
            sizes[comboBoxContext.size].text
          )}
        />

        {shortcut && (
          <div
            aria-hidden='true'
            className={cx(
              'absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8',
              shortcutClassName
            )}
          >
            <span
              className={cx(
                'pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset',
                isDisabled && 'bg-transparent text-disabled'
              )}
            >
              ⌘K
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const MultiSelectTagsValue = ({
  size,
  shortcut,
  placeholder,
  shortcutClassName,
  placeholderIcon: Icon = SearchLg,
  // Omit this prop to avoid invalid HTML attribute warning
  isDisabled: _isDisabled,
  ...otherProps
}: ComboBoxValueProps) => {
  return (
    <AriaGroup
      {...otherProps}
      className={({
        isFocusWithin,
        isDisabled,
      }: {
        isFocusWithin: boolean;
        isDisabled: boolean;
      }) =>
        cx(
          'relative flex w-full items-center gap-2 rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset',
          isDisabled && 'cursor-not-allowed bg-disabled_subtle',
          isFocusWithin && 'ring-2 ring-brand',
          sizes[size].root
        )
      }
    >
      {({ isDisabled }: { isDisabled: boolean }) => (
        <>
          {Icon && (
            <Icon
              className={cx(
                'pointer-events-none shrink-0 text-fg-quaternary',
                sizes[size].icon
              )}
            />
          )}
          <FocusScope contain={false} autoFocus={false} restoreFocus={false}>
            <InnerMultiSelect
              isDisabled={isDisabled}
              size={size}
              shortcut={shortcut}
              shortcutClassName={shortcutClassName}
              placeholder={placeholder}
            />
          </FocusScope>
        </>
      )}
    </AriaGroup>
  );
};

const MultiSelect = MultiSelectBase as typeof MultiSelectBase & {
  Item: typeof SelectItem;
};

MultiSelect.Item = SelectItem;

export { MultiSelect as MultiSelect };
